import classNames from 'classnames';
import isEmpty from 'lodash/fp/isEmpty';
import omit from 'lodash/fp/omit';
import React, { useState } from 'react';

import Button from '@rio-cloud/rio-uikit/Button';
import ButtonDropdown from '@rio-cloud/rio-uikit/ButtonDropdown';
import Notification from '@rio-cloud/rio-uikit/Notification';
import NotFoundState from '@rio-cloud/rio-uikit/NotFoundState';
import SortArrows from '@rio-cloud/rio-uikit/SortArrows';
import SortDirection, { type SortDirectionType } from '@rio-cloud/rio-uikit/SortDirection';
import { naturalSortByProperty } from '@rio-cloud/rio-uikit/SortUtils';
import TableCardsSorting from '@rio-cloud/rio-uikit/TableCardsSorting';
import TableSearch from '@rio-cloud/rio-uikit/TableSearch';
import TableSettingsDialog from '@rio-cloud/rio-uikit/TableSettingsDialog';
import TableToolbar from '@rio-cloud/rio-uikit/TableToolbar';
import TableViewToggles, { type TableViewTogglesViewType } from '@rio-cloud/rio-uikit/TableViewToggles';

import { IChallenges } from '../../../../../types/challenges';

const defaultColumnOrder = ['name', 'description', 'points', 'status', 'criterion', 'criterionValue', 'type', 'validUntil'];
const disabledColumns: string[] = [];

type ColumnLabel = {
  [key: string]: string;
};

const columnLabels: ColumnLabel = {
  name: 'Nome',
  description: 'Descrição',
  points: 'Pontos',
  status: 'Status',
  criterion: 'Critério',
  criterionValue: 'Valor',
  type: 'Tipo',
  validUntil: 'Válido Até',
};

type ColumnDetails = {
  [key: string]: number;
};

type ColumnDetailsMap = {
  [key: string]: ColumnDetails;
};

const demoColumnsDetails: ColumnDetailsMap = {
  name: {
    width: 200,
    defaultWidth: 200,
    maxWidth: 300,
  },
  description: {
    width: 300,
    defaultWidth: 300,
    maxWidth: 500,
  },
  points: {
    width: 100,
    defaultWidth: 100,
    maxWidth: 150,
  },
  status: {
    width: 120,
    defaultWidth: 120,
    maxWidth: 150,
  },
  criterion: {
    width: 150,
    defaultWidth: 150,
    maxWidth: 200,
  },
  criterionValue: {
    width: 100,
    defaultWidth: 100,
    maxWidth: 150,
  },
  type: {
    width: 120,
    defaultWidth: 120,
    maxWidth: 150,
  },
  validUntil: {
    width: 120,
    defaultWidth: 120,
    maxWidth: 150,
  },
};

interface CustomTableProps {
  data: IChallenges[];
  page: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  sortBy: string;
  sortDir: 'asc' | 'desc';
  onSort: (key: string) => void;
  searchValue: string;
  onSearchChange: (v: string) => void;
  onEditChallenge?: (challenge: IChallenges) => void;
  onCreateEdit?: () => void;
  onToggleStatus?: (challengeId: string, currentStatus: boolean) => Promise<boolean>;
}

const CustomTable: React.FC<CustomTableProps> = ({
  data,
  sortBy,
  sortDir,
  onSort,
  searchValue,
  onSearchChange,
  onEditChallenge,
  onCreateEdit,
  onToggleStatus,
}) => {
  const [columnOrder, setColumnOrder] = useState<string[]>(defaultColumnOrder);
  const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);
  const [columnsDetails, setColumnsDetails] = useState(demoColumnsDetails);
  const [viewType, setViewType] = useState<TableViewTogglesViewType>(TableViewToggles.VIEW_TYPE_TABLE);
  const [showTableSettingsDialog, setShowTableSettingsDialog] = useState(false);

  const handleToggleTableSettingsDialog = () => setShowTableSettingsDialog(!showTableSettingsDialog);
  const handleViewTypeChange = (newViewType: TableViewTogglesViewType) => setViewType(newViewType);
  const handleSearchValueChange = (newSearchValue: string) => onSearchChange(newSearchValue);

  const handleColumnChange = (
    newColumnOrder: string[],
    newHiddenColumns: string[],
    newColumnsDetails = columnsDetails
  ) => {
    setColumnOrder(newColumnOrder);
    setHiddenColumns(newHiddenColumns);
    setColumnsDetails(newColumnsDetails);
  };

  const handleColumnDetailsChange = (column: string, newColumnDetails: ColumnDetails) => {
    const updatedColumnsDetails = { ...columnsDetails };
    updatedColumnsDetails[column] = newColumnDetails;
    setColumnsDetails(updatedColumnsDetails);
  };

  const getSortDir = (sortDir: SortDirectionType, sortBy: string, previousSortBy: string) => {
    if (sortBy === previousSortBy) {
      return sortDir === SortDirection.ASCENDING ? SortDirection.DESCENDING : SortDirection.ASCENDING;
    }
    return SortDirection.ASCENDING;
  };

  const handleSortChange = (event: React.MouseEvent<HTMLElement>) => {
    const newSortBy = event.currentTarget.getAttribute('data-sortby');
    if (newSortBy) {
      handleCardSortChange(newSortBy, getSortDir(sortDir as SortDirectionType, newSortBy, sortBy));
    }
  };

  const handleCardSortChange = (newSortBy: string, newSortDir: SortDirectionType) => {
    onSort(newSortBy);
  };

  const renderTableHead = (column: string, label: string, sortByColumn: string, sortDirection: SortDirectionType) => {
    const tableHeadClassNames = classNames('user-select-none', 'sort-column');

    return (
      <th
        key={column}
        className={tableHeadClassNames}
        onClick={handleSortChange}
        data-field={column}
        data-sortby={column}
        title={label}
      >
        <span>
          {sortByColumn === column ? <SortArrows direction={sortDirection} /> : <SortArrows />}
          <span>{label}</span>
        </span>
      </th>
    );
  };

  const renderTableCaption = (column: string, columnDetails: ColumnDetails) => {
    const style = columnDetails?.width
      ? {
        minWidth: columnDetails.width,
        width: columnDetails.width,
      }
      : {};

    return <col key={column} style={style} />;
  };

  const typeLabels: Record<string, string> = {
    DEFAULT: 'Padrão',
    PERSONALIZED: 'Personalizado',
  };

  const criterionLabels: Record<string, string> = {
    BETWEEN: 'Entre',
    LESS_EQUAL: 'Menor ou igual',
    EQUAL: 'Igual',
    GREATER_EQUAL: 'Maior ou igual',
  };

  const renderStatusChip = (status: boolean) => {
    const config = status
      ? {
        text: 'Ativo',
        className: 'badge badge-success'
      }
      : {
        text: 'Inativo',
        className: 'badge badge-secondary'
      };

    return (
      <span className={config.className}>
        {config.text}
      </span>
    );
  };

  const renderTableRow = (challenge: IChallenges, index: number) => {
    const typeLabel = typeLabels[challenge.type] ?? challenge.type;
    const critLabel = criterionLabels[challenge.criterion] ?? challenge.criterion;
    const settingsIcon = <span className="rioglyph rioglyph-settings margin-right-8" />;

    return (
      <tr key={`${challenge.name}-${index}`}>
        <td className="font-bold">{challenge.name}</td>
        <td>{challenge.description}</td>
        <td>+{challenge.points} Pontos</td>
        <td>{renderStatusChip(challenge.active)}</td>
        <td>
          {settingsIcon}
          {critLabel}
        </td>
        <td>{challenge.criterionValue}</td>
        <td>{typeLabel}</td>
        <td>
          {challenge.validUntil
            ? new Date(challenge.validUntil).toLocaleDateString('pt-BR')
            : ''}
        </td>
        <td className="table-action">
          <ButtonDropdown
            title={<span className="rioglyph rioglyph-option-vertical" />}
            variant="link"
            iconOnly
            items={[
              {
                value: (
                  <div>
                    <span className="rioglyph rioglyph-pencil margin-right-10" />
                    <span>Editar</span>
                  </div>
                ),
                onSelect: () => onEditChallenge?.(challenge),
              },
              {
                value: (
                  <div>
                    <span className="rioglyph rioglyph-settings margin-right-10" />
                    <span>{challenge.active ? 'Inativar' : 'Ativar'}</span>
                  </div>
                ),
                onSelect: async () => {
                  if (onToggleStatus) {
                    const success = await onToggleStatus(challenge.id.toString(), challenge.active);
                    if (success) {
                      Notification.success(
                        `Desafio ${challenge.active ? 'inativado' : 'ativado'} com sucesso!`
                      );
                    } else {
                      Notification.error(
                        `Erro ao ${challenge.active ? 'inativar' : 'ativar'} o desafio. Tente novamente.`
                      );
                    }
                  }
                },
              },
            ]}
          />
        </td>
      </tr>
    );
  };

  const columns = columnOrder.filter(name => !hiddenColumns.includes(name));

  const withoutHidden = omit(hiddenColumns);
  const filteredRows = data.map(challenge => ({ ...withoutHidden(challenge) })) as IChallenges[];

  const searchResult = !searchValue
    ? filteredRows
    : filteredRows.filter((row: Partial<IChallenges>) =>
      columns.some(col => {
        const value = row[col as keyof IChallenges];
        if (value == null) {
          return false;
        }
        return row[col as keyof IChallenges]?.toString().toLowerCase().includes(searchValue.toLowerCase());
      })
    );

  const rows = sortBy ? naturalSortByProperty(searchResult, sortBy as keyof IChallenges, sortDir as SortDirectionType) : searchResult;

  const tableClassNames = classNames(
    'table',
    'table-layout-fixed',
    'table-column-overflow-hidden',
    'table-bordered',
    'table-sticky',
    'table-head-filled',
    viewType === TableViewToggles.VIEW_TYPE_SINGLE_CARD && 'table-cards table-single-card',
    viewType === TableViewToggles.VIEW_TYPE_MULTI_CARDS && 'table-cards table-multi-cards'
  );

  const isViewTypeTable = viewType === TableViewToggles.VIEW_TYPE_TABLE;

  const cardSortingSelectOptions = columns.map(column => {
    return {
      id: column,
      label: columnLabels[column],
      selected: column === sortBy,
      disabled: false,
    };
  });

  return (
    <div>
      <TableToolbar>
        <div className="table-toolbar-container">
          <div className="table-toolbar-group-left">
            <div className="table-toolbar-column">
              <div className="btn-toolbar table-btn-toolbar">
                <Button bsStyle={Button.PRIMARY} iconName="rioglyph-plus" onClick={onCreateEdit}>
                  Novo Desafio
                </Button>
              </div>
            </div>
            <div className="table-toolbar-column">
              <TableSearch
                value={searchValue}
                onChange={handleSearchValueChange}
                placeholder="Buscar nos desafios..."
              />
            </div>
          </div>
          <div className="table-toolbar-group-right">
            <div className="table-toolbar-column table-toolbar-column-spacer">
              <label className="table-toolbar-label">Visualização</label>
              <TableViewToggles
                initialViewType={viewType}
                onViewTypeChange={handleViewTypeChange}
                tableViewTooltipContent="Visualização em tabela"
                singleCardViewTooltipContent="Visualização em lista"
                multiCardsViewTooltipContent="Visualização em cards"
              />
            </div>
            <div className="table-toolbar-column">
              <Button iconOnly iconName="rioglyph-settings" onClick={handleToggleTableSettingsDialog} />
            </div>
          </div>
          {showTableSettingsDialog && (
            <TableSettingsDialog
              show={showTableSettingsDialog}
              title="Configurações da tabela"
              onHide={handleToggleTableSettingsDialog}
              onColumnChange={handleColumnChange}
              defaultColumnOrder={defaultColumnOrder}
              defaultHiddenColumns={[]}
              columnOrder={columnOrder}
              hiddenColumns={hiddenColumns}
              columnLabels={columnLabels}
              disabledColumns={disabledColumns}
              closeButtonText="Fechar"
              resetButtonText="Restaurar padrão"
              searchPlaceholder="Buscar por nome da coluna"
              notFoundMessage="Nenhuma coluna encontrada"
              columnsDetails={columnsDetails}
              autoLabel="Automático"
              onColumnDetailsChange={handleColumnDetailsChange}
              onSearchChange={(val: string) => {
              }}
              immediateChange
            />
          )}
        </div>
      </TableToolbar>
      {sortBy && !isViewTypeTable && (
        <TableCardsSorting
          selectOptions={cardSortingSelectOptions}
          sortName={sortBy}
          sortOrder={sortDir as SortDirectionType}
          onSortChange={handleCardSortChange}
        />
      )}
      <div>
        {isEmpty(rows) && !isViewTypeTable && (
          <NotFoundState headline="Nada encontrado" message="Refine sua busca" />
        )}
        <table className={tableClassNames}>
          <colgroup>
            {columns.map(column => renderTableCaption(column, columnsDetails[column]))}
            <col className="table-action" />
          </colgroup>
          <thead>
            <tr>
              {columns.map(column => renderTableHead(column, columnLabels[column], sortBy, sortDir as SortDirectionType))}
              <th className="table-action" />
            </tr>
          </thead>
          <tbody>
            {isEmpty(rows) && isViewTypeTable && (
              <tr>
                <td colSpan={columns.length + 1}>
                  <NotFoundState
                    outerClassName="border-none"
                    headline="Nada encontrado"
                    message="Refine sua busca"
                  />
                </td>
              </tr>
            )}
            {rows.map((row: IChallenges, index: number) => renderTableRow(row, index))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default React.memo(CustomTable);